import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  TrendingUp, TrendingDown, Calendar, Brain,
  AlertTriangle, CheckCircle, Zap, Printer, ChevronDown, ChevronUp
} from 'lucide-react'
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  Area, AreaChart, CartesianGrid, Legend, ReferenceLine
} from 'recharts'
import api from '../services/api'
import { CashflowModuleCard } from '../components/FinancialIntelligencePanel'
import { useCashflowForecast } from '../hooks/useFinancialIntelligence'
import { useWorkspace } from '../hooks/useWorkspace'
import { useLanguage } from '../hooks/useLanguage'
import TourGuide, { TourStep } from '../components/TourGuide'
import {
  getDefaultForecastEngineConfig,
  runForecastEngine,
  type ForecastEngineOutput,
  type MonthlyData,
  type ForecastPoint,
} from '../lib/forecastEngine'

type ForecastData = ForecastEngineOutput

function calculateForecasts(
  historical: MonthlyData[],
  monthsToPredict: number = 3,
): ForecastData {
  const config = getDefaultForecastEngineConfig({
    useLegacyConfidenceFallback: true,
  })

  return runForecastEngine({
    historical,
    monthsToPredict,
    config,
  })
}

export default function Forecasts() {
  const { t, language } = useLanguage()
  const { currentWorkspace } = useWorkspace()
  const [monthsToPredict, setMonthsToPredict] = useState(3)

  // Fetch historical data (last 12 months)
  const { data: timelineData, isLoading } = useQuery({
    queryKey: ['timeline-forecast'],
    queryFn: async () => {
      const now = new Date()
      const startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1)
      const response = await api.get(`/reports/timeline?start_date=${startDate.toISOString().split('T')[0]}&end_date=${now.toISOString().split('T')[0]}`)
      return response.data
    }
  })

  // Fetch fixed vs variable data for better predictions
  const { data: fixedVarData } = useQuery({
    queryKey: ['fixed-var-forecast'],
    queryFn: async () => {
      const now = new Date()
      const startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1)
      const response = await api.get(`/reports/fixed-vs-variable?start_date=${startDate.toISOString().split('T')[0]}&end_date=${now.toISOString().split('T')[0]}`)
      return response.data
    }
  })

  // Combine data and calculate forecasts
  const historical: MonthlyData[] = []
  if (timelineData?.data) {
    const timeline = timelineData.data as Array<{ month: string; label: string; income: number; expenses: number }>
    const fixedVar = (fixedVarData?.data ?? []) as Array<{ month: string; fixed: number; variable: number }>
    const fixedVarByMonth = new Map(fixedVar.map(item => [item.month, item]))

    for (const t of timeline) {
      const fv = fixedVarByMonth.get(t.month)
      historical.push({
        month: t.month,
        label: t.label,
        income: t.income,
        expenses: t.expenses,
        fixed: fv?.fixed ?? 0,
        variable: fv?.variable ?? Math.max(0, t.expenses)
      })
    }
  }

  const forecastData = calculateForecasts(historical, monthsToPredict)
  const canonicalCashflowPayload = useMemo(() => {
    const finalHorizon = Math.max(30, monthsToPredict * 30)
    const horizons = Array.from(new Set([7, 30, 90, finalHorizon])).sort((a, b) => a - b)
    return {
      ensure_backfill: true,
      lookback_days: 180,
      horizons_days: horizons,
      use_runpod_tft: true,
      model_version: 'tft-v0',
    }
  }, [monthsToPredict])

  const aiCashflowQuery = useCashflowForecast({
    enabled: !!currentWorkspace?.id,
    source: 'canonical',
    canonicalPayload: canonicalCashflowPayload,
  })

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(language === 'it' ? 'it-IT' : 'en-US', { style: 'currency', currency: 'EUR' }).format(value)

  // Deduplicate and consolidate all alerts (insights + warnings)
  const allAlerts = useMemo(() => {
    const alerts: Array<{ type: 'warning' | 'success' | 'info'; title: string; message: string; severity: number }> = []
    const seen = new Set<string>()

    // Add insights (deduplicated)
    for (const insight of forecastData.insights) {
      // Skip the "Vincoli economici applicati: N segnalazioni" — we show individual warnings instead
      if (insight.message.includes('segnalazioni di stabilita')) continue
      const key = insight.message
      if (seen.has(key)) continue
      seen.add(key)
      alerts.push({
        type: insight.type as 'warning' | 'success' | 'info',
        title: insight.title,
        message: insight.message,
        severity: insight.type === 'warning' ? 5 : 2,
      })
    }

    // Add warnings — deduplicate by message text (remove scenario prefix for dedup)
    const warningMessages = new Map<string, string[]>()
    for (const w of forecastData.warnings) {
      // Extract scenario name and core message
      const match = w.match(/^Scenario (\w+): (.+)$/)
      if (match) {
        const [, scenario, core] = match
        if (!warningMessages.has(core)) warningMessages.set(core, [])
        warningMessages.get(core)!.push(scenario)
      } else {
        if (!seen.has(w)) {
          seen.add(w)
          alerts.push({ type: 'warning', title: 'Vincolo economico', message: w, severity: 3 })
        }
      }
    }
    // Merge deduplicated warnings with scenario list
    for (const [core, scenarios] of warningMessages) {
      const scenarioList = scenarios.length === 3 ? 'Tutti gli scenari' : scenarios.map(s =>
        s === 'base' ? 'Base' : s === 'optimistic' ? 'Ottimistico' : 'Stress'
      ).join(', ')
      alerts.push({
        type: 'warning',
        title: 'Vincolo economico applicato',
        message: `${scenarioList}: ${core}`,
        severity: 3,
      })
    }

    // Sort: highest severity first
    alerts.sort((a, b) => b.severity - a.severity)
    return alerts
  }, [forecastData.insights, forecastData.warnings])

  const riskCount = allAlerts.filter(a => a.type === 'warning').length
  const [insightsOpen, setInsightsOpen] = useState(false)

  // Combine historical + forecasts for chart
  const chartData = [
    ...forecastData.historical.map(h => ({
      month: h.label,
      expenses: h.expenses,
      income: h.income,
      type: 'actual'
    })),
    ...forecastData.forecasts.map(f => ({
      month: f.label,
      expenses: f.predicted_expenses,
      income: f.predicted_income,
      predicted_expenses: f.predicted_expenses,
      predicted_income: f.predicted_income,
      type: 'forecast'
    }))
  ]

  const summarizeScenario = (points: ForecastPoint[]) => {
    const income = points.reduce((acc, point) => acc + point.predicted_income, 0)
    const expenses = points.reduce((acc, point) => acc + point.predicted_expenses, 0)
    return {
      income,
      expenses,
      delta: income - expenses,
    }
  }

  const scenarioSummary = {
    base: summarizeScenario(forecastData.forecast_scenarios.base),
    optimistic: summarizeScenario(forecastData.forecast_scenarios.optimistic),
    pessimistic: summarizeScenario(forecastData.forecast_scenarios.pessimistic),
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Tour steps for Forecasts
  const tourSteps: TourStep[] = language === 'it' ? [
    {
      title: 'Previsioni con IA! 🔮',
      content: 'Questa sezione usa algoritmi predittivi per stimare le tue spese future basandosi sui dati storici.',
      position: 'center'
    },
    {
      target: '[data-tour="forecast-chart"]',
      title: 'Grafico Previsionale',
      content: 'Il grafico mostra i dati storici (linea continua) e le previsioni future (area tratteggiata). L\'intervallo di confidenza è mostrato in gradiente.',
      position: 'top'
    },
    {
      target: '[data-tour="forecast-insights"]',
      title: 'Insights AI',
      content: 'Suggerimenti automatici basati sull\'analisi dei tuoi pattern di spesa. Ti aiutano a prendere decisioni migliori.',
      position: 'left'
    }
  ] : [
    {
      title: 'AI Forecasting! 🔮',
      content: 'This section uses predictive algorithms to estimate your future expenses based on historical data.',
      position: 'center'
    },
    {
      target: '[data-tour="forecast-chart"]',
      title: 'Forecast Chart',
      content: 'The chart shows historical data (solid line) and future predictions (dashed area). Confidence interval is shown in gradient.',
      position: 'top'
    },
    {
      target: '[data-tour="forecast-insights"]',
      title: 'AI Insights',
      content: 'Automatic suggestions based on analysis of your spending patterns. They help you make better decisions.',
      position: 'left'
    }
  ]

  return (
    <>
      <TourGuide tourId="forecasts" steps={tourSteps} />
      <div className="space-y-6 pt-1">
        {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t.nav.forecasts}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">{t.forecasts.subtitle}</p>
        </div>

        {/* Print and Months selector */}
        <div className="flex items-center gap-2 no-print">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            {t.dashboard.print}
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t.forecasts.nextMonths}:</span>
            <select
              value={monthsToPredict}
              onChange={(e) => setMonthsToPredict(Number(e.target.value))}
              className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 dark:text-white"
            >
              <option value={1}>1 mese</option>
              <option value={3}>3 mesi</option>
              <option value={6}>6 mesi</option>
              <option value={12}>12 mesi</option>
            </select>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card !bg-gradient-to-br !from-blue-50 !to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-blue-600">Media Spese Mensili</p>
              <p className="kpi-value kpi-animate text-blue-900">
                {formatCurrency(forecastData.statistics.avg_monthly_expenses)}
              </p>
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium ${
              forecastData.statistics.expense_trend > 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              {forecastData.statistics.expense_trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {Math.abs(forecastData.statistics.expense_trend).toFixed(1)}%
            </div>
          </div>
        </div>

        <div className="card !bg-gradient-to-br !from-green-50 !to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-green-600">Media Entrate Mensili</p>
              <p className="kpi-value kpi-animate text-green-900">
                {formatCurrency(forecastData.statistics.avg_monthly_income)}
              </p>
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium ${
              forecastData.statistics.income_trend > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {forecastData.statistics.income_trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {Math.abs(forecastData.statistics.income_trend).toFixed(1)}%
            </div>
          </div>
        </div>

        <div className="card !bg-gradient-to-br !from-purple-50 !to-purple-100 border-purple-200">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-purple-600">Risparmio Medio</p>
            <p className={`kpi-value kpi-animate ${
              forecastData.statistics.avg_monthly_income - forecastData.statistics.avg_monthly_expenses >= 0 
                ? 'text-purple-900' : 'text-red-600'
            }`}>
              {formatCurrency(forecastData.statistics.avg_monthly_income - forecastData.statistics.avg_monthly_expenses)}
            </p>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-cyan-700">Volatilità Spese</p>
            <p className="kpi-value kpi-animate text-cyan-900">
              {forecastData.statistics.volatility.toFixed(0)}%
            </p>
            <p className="text-xs text-cyan-700">
              {forecastData.statistics.volatility < 15 ? 'Molto stabile' : 
               forecastData.statistics.volatility < 30 ? 'Normale' : 'Alta variabilità'}
            </p>
          </div>
        </div>
      </div>

      <CashflowModuleCard
        data={aiCashflowQuery.data}
        isLoading={aiCashflowQuery.isLoading}
        isFetching={aiCashflowQuery.isFetching}
        error={aiCashflowQuery.error}
        onRetry={() => {
          void aiCashflowQuery.refetch()
        }}
        subtitle="Previsione AI servita dal backend production, in parallelo al motore previsionale locale della pagina."
      />

      {/* Analisi Intelligente — collapsible accordion with notification badge */}
      {allAlerts.length > 0 && (
        <div data-tour="forecast-insights" className="rounded-2xl border border-gray-200 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/60 backdrop-blur-sm overflow-hidden">
          <button
            onClick={() => setInsightsOpen(!insightsOpen)}
            className="w-full flex items-center gap-2.5 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
          >
            <Brain className="w-4 h-4 text-purple-500" />
            <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Analisi Intelligente
            </h3>
            {/* Red notification badge */}
            {riskCount > 0 && (
              <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse">
                {riskCount}
              </span>
            )}
            {allAlerts.length > riskCount && (
              <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-500 text-white text-[10px] font-bold">
                {allAlerts.length - riskCount}
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {riskCount > 0 && !insightsOpen && (
                <span className="text-[10px] text-red-500 font-medium hidden sm:block">
                  {riskCount} {riskCount === 1 ? 'rischio' : 'rischi'}
                </span>
              )}
              {insightsOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </button>
          {insightsOpen && (
            <div className="divide-y divide-gray-100 dark:divide-slate-700/40 border-t border-gray-100 dark:border-slate-700/50">
              {allAlerts.map((alert, index) => {
                const isWarning = alert.type === 'warning'
                const isSuccess = alert.type === 'success'
                return (
                  <div
                    key={index}
                    className={`flex items-start gap-3 px-5 py-3 ${
                      isWarning ? 'bg-amber-50/50 dark:bg-amber-950/10' :
                      isSuccess ? 'bg-emerald-50/50 dark:bg-emerald-950/10' :
                      'bg-sky-50/50 dark:bg-sky-950/10'
                    }`}
                  >
                    <div className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                      isWarning ? 'bg-amber-100 dark:bg-amber-900/40' :
                      isSuccess ? 'bg-emerald-100 dark:bg-emerald-900/40' :
                      'bg-sky-100 dark:bg-sky-900/40'
                    }`}>
                      {isWarning ? (
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                      ) : isSuccess ? (
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <Zap className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold leading-tight ${
                        isWarning ? 'text-amber-800 dark:text-amber-300' :
                        isSuccess ? 'text-emerald-800 dark:text-emerald-300' :
                        'text-sky-800 dark:text-sky-300'
                      }`}>
                        {alert.title}
                      </p>
                      <p className={`text-xs mt-0.5 leading-relaxed ${
                        isWarning ? 'text-amber-700/80 dark:text-amber-400/70' :
                        isSuccess ? 'text-emerald-700/80 dark:text-emerald-400/70' :
                        'text-sky-700/80 dark:text-sky-400/70'
                      }`}>
                        {alert.message}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Main Chart */}
      <div data-tour="forecast-chart" className="card">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Storico e Previsioni
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis 
                tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                formatter={(value: number) => formatCurrency(value)}
                labelStyle={{ fontWeight: 'bold' }}
              />
              <Legend />
              
              {/* Reference line for last actual month */}
              {forecastData.historical.length > 0 && (
                <ReferenceLine 
                  x={forecastData.historical[forecastData.historical.length - 1]?.label} 
                  stroke="#9333ea" 
                  strokeDasharray="5 5"
                  label={{ value: 'Oggi', position: 'top', fill: '#9333ea', fontSize: 12 }}
                />
              )}
              
              <Area
                type="monotone"
                dataKey="income"
                name="Entrate"
                stroke="#22c55e"
                fill="#22c55e"
                fillOpacity={0.2}
              />
              <Area
                type="monotone"
                dataKey="expenses"
                name="Spese"
                stroke="#ef4444"
                fill="#ef4444"
                fillOpacity={0.2}
              />
              <Area
                type="monotone"
                dataKey="predicted_expenses"
                name="Spese Previste"
                stroke="#9333ea"
                fill="#9333ea"
                fillOpacity={0.3}
                strokeDasharray="5 5"
              />
              <Area
                type="monotone"
                dataKey="predicted_income"
                name="Entrate Previste"
                stroke="#14b8a6"
                fill="#14b8a6"
                fillOpacity={0.3}
                strokeDasharray="5 5"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Forecasts Detail */}
      {forecastData.forecasts.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Dettaglio Previsioni
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {forecastData.forecasts.map((forecast) => (
              <div 
                key={forecast.month}
                className="p-4 rounded-lg border border-gray-200 dark:border-slate-700 bg-gradient-to-br from-gray-50 to-white dark:from-slate-800 dark:to-slate-800"
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 dark:text-white">{forecast.label}</h4>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      forecast.trend === 'up' ? 'bg-red-100 text-red-700' :
                      forecast.trend === 'down' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {forecast.trend === 'up' ? '↑ In aumento' :
                       forecast.trend === 'down' ? '↓ In calo' : '→ Stabile'}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Spese previste:</span>
                    <span className="font-medium text-red-600">{formatCurrency(forecast.predicted_expenses)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Entrate previste:</span>
                    <span className="font-medium text-green-600">{formatCurrency(forecast.predicted_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Di cui fissi:</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(forecast.predicted_fixed)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Di cui variabili:</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrency(forecast.predicted_variable)}</span>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Affidabilità previsione</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            forecast.confidence > 70 ? 'bg-green-500' :
                            forecast.confidence > 50 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${forecast.confidence}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium">{forecast.confidence}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scenario Summary */}
      {forecastData.forecast_scenarios.base.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
            Scenari previsionali (orizzonte selezionato)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Base</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Entrate totali: <span className="font-semibold text-green-600">{formatCurrency(scenarioSummary.base.income)}</span></p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Spese totali: <span className="font-semibold text-red-600">{formatCurrency(scenarioSummary.base.expenses)}</span></p>
              <p className="text-sm mt-1">Saldo: <span className={`font-semibold ${scenarioSummary.base.delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(scenarioSummary.base.delta)}</span></p>
            </div>
            <div className="p-4 rounded-lg border border-green-200 dark:border-green-900 bg-green-50/60 dark:bg-green-950/20">
              <p className="text-xs uppercase tracking-wide text-green-700 dark:text-green-400 mb-2">Ottimistico</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">Entrate totali: <span className="font-semibold text-green-600">{formatCurrency(scenarioSummary.optimistic.income)}</span></p>
              <p className="text-sm text-gray-700 dark:text-gray-300">Spese totali: <span className="font-semibold text-red-600">{formatCurrency(scenarioSummary.optimistic.expenses)}</span></p>
              <p className="text-sm mt-1">Saldo: <span className={`font-semibold ${scenarioSummary.optimistic.delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(scenarioSummary.optimistic.delta)}</span></p>
            </div>
            <div className="p-4 rounded-lg border border-yellow-200 dark:border-yellow-900 bg-yellow-50/60 dark:bg-yellow-950/20">
              <p className="text-xs uppercase tracking-wide text-yellow-800 dark:text-yellow-400 mb-2">Stress</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">Entrate totali: <span className="font-semibold text-green-600">{formatCurrency(scenarioSummary.pessimistic.income)}</span></p>
              <p className="text-sm text-gray-700 dark:text-gray-300">Spese totali: <span className="font-semibold text-red-600">{formatCurrency(scenarioSummary.pessimistic.expenses)}</span></p>
              <p className="text-sm mt-1">Saldo: <span className={`font-semibold ${scenarioSummary.pessimistic.delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(scenarioSummary.pessimistic.delta)}</span></p>
            </div>
          </div>
        </div>
      )}

      {/* Risk simulation */}
      {forecastData.risk_simulation && (
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
            Simulazione rischio (Monte Carlo)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg border border-gray-200 dark:border-slate-700">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Iterazioni</p>
              <p className="kpi-value kpi-animate text-gray-900 dark:text-white">{forecastData.risk_simulation.iterations}</p>
            </div>
            <div className="p-4 rounded-lg border border-gray-200 dark:border-slate-700">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Probabilità crisi liquidità</p>
              <p className={`kpi-value kpi-animate ${forecastData.risk_simulation.liquidity_crisis_probability > 40 ? 'text-red-600' : forecastData.risk_simulation.liquidity_crisis_probability > 20 ? 'text-yellow-600' : 'text-green-600'}`}>
                {forecastData.risk_simulation.liquidity_crisis_probability.toFixed(1)}%
              </p>
            </div>
            <div className="p-4 rounded-lg border border-gray-200 dark:border-slate-700">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Intervallo 80% (p10)</p>
              <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
                {formatCurrency(forecastData.risk_simulation.confidence_interval_80.p10)}
              </p>
            </div>
            <div className="p-4 rounded-lg border border-gray-200 dark:border-slate-700">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Intervallo 80% (p90)</p>
              <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
                {formatCurrency(forecastData.risk_simulation.confidence_interval_80.p90)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Methodology note */}
      <div className="text-center text-xs text-gray-400 dark:text-gray-500 py-4 font-light tracking-wide">
        <p>
          Le previsioni usano Holt-Winters (con fallback automatici su storico corto), scenario engine,
          vincoli economici e confidence score dinamico basato su volatilità, qualità dati e dispersione degli scenari.
        </p>
      </div>
    </div>
    </>
  )
}
